import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// models
import {
  CreatePersonPhoneCommand,
  UpdatePersonPhoneCommand
} from '../../../models/Entities/Person/Phone/PhoneModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// functions
import * as ownFunctions from './PersonPhone.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonPhoneFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  useEffect(() => {
    if (props.phone) {
      setPhoneNumber(props.phone.phoneNumber || '');
    } else {
      resetForm();
    }
  }, [props.phone, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setPhoneNumber('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!phoneNumber.trim()) {
      errors.push(t('persons.phones.form.validation.required', { defaultValue: 'El teléfono es obligatorio' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonPhoneCommand = {
      personId: props.PersonId,
      phoneNumber: phoneNumber.trim()
    };

    if (props.phone) {
      props.UpdatePhone({ ...payload, id: props.phone.id } as UpdatePersonPhoneCommand);
    } else {
      props.CreatePhone(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const isSaving = !!(props.isAdding || props.isUpdating);

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      eyebrow={props.personName || undefined}
      title={
        props.phone
          ? t('persons.phones.form.editTitle', { defaultValue: 'Editar teléfono' })
          : t('persons.phones.form.addTitle', { defaultValue: 'Nuevo teléfono' })
      }
      onDismiss={handleCancel}
      preventDismiss={isSaving}
      testId="person-phone-drawer"
      secondaryAction={{ label: t('persons.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('persons.form.footer.save', { defaultValue: 'Guardar' }),
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {submitted && validation && !validation.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}</MessageBarTitle>
            <ul>
              {validation.errors.map((error: string, index: number) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {submitted && (props.failAdding || props.failUpdating) && props.error && (
        <ErrorMessageComponent Error={props.error} />
      )}

      <GuideDrawerForm onSubmit={handleSave}>
        <GuideField
          label={t('persons.phones.form.phoneNumber', { defaultValue: 'Número de teléfono' })}
          required
          error={
            submitted && !phoneNumber.trim()
              ? t('persons.phones.form.validation.required', { defaultValue: 'El teléfono es obligatorio' })
              : undefined
          }
        >
          <Input type="tel" value={phoneNumber} onChange={(_, data) => setPhoneNumber(data.value || '')} />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonPhoneFormComponent as any);
